import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
  Divider,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Chip,
  Alert,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Build as BuildIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { workloadsApi } from '../services/workloadsService';
import { extractErrorMessage } from '../services/api';
import {
  WorkloadDefinition,
  WorkloadCategory,
  ApplicationCategory,
  Application,
  PackerConfig,
  GpuSeries,
  RuntimeConfig,
} from '../services/types';
import { TableSkeleton } from '../components/SkeletonLoaders';
import ErrorMessage from '../components/ErrorMessage';
import { useApiQuery } from '../hooks/useApiQuery';
import { useApiMutation } from '../hooks/useApiMutation';
import { useAuth } from '../contexts/AuthContext';
import BuildImageDialog from '../components/BuildImageDialog';
import TrainingFilesTab from '../components/workload/TrainingFilesTab';
import BuildReadinessChecklist from '../components/workload/BuildReadinessChecklist';
import { NC_636_TRAINING_FILES_ENABLED } from '../config/featureFlags';
import { isBuildable, isStagingOnly } from '../config/workloadCapabilities';
import { evaluateBuildReadiness } from '../utils/workloadReadiness';
import { SelectWithCustom } from '../components/SelectWithCustom';

// M20-D3 (NC-974): run-time VM sizing UI. The Azure Sub + Region are DERIVED
// from the GPU series server-side (run-lifecycle 110), so we only surface the
// derived region read-only and never let the admin pick it. Curated SKUs are
// suggestions; the API (shared/runtimeConfigValidation.ts) is the source of
// truth and rejects an inconsistent vmSize<->gpuSeries pairing with a 400.
const GPU_SERIES_OPTIONS: { value: GpuSeries; label: string }[] = [
  { value: 'none', label: 'Keine GPU' },
  { value: 'a10', label: 'NVIDIA A10' },
  { value: 'v710', label: 'AMD Radeon PRO V710' },
];

// Derived Azure region per series — display only (mirrors the BE derivation).
const REGION_BY_SERIES: Record<GpuSeries, string> = {
  a10: 'Germany West Central (germanywestcentral)',
  v710: 'West Europe (westeurope)',
  none: 'Standard-Region (aus Konfiguration)',
};

// Curated VM SKUs per series. Each entry satisfies the BE plausibility check
// (A10 SKUs contain "A10", V710 SKUs contain "V710", none contains neither).
const CURATED_VM_SIZES: Record<GpuSeries, string[]> = {
  none: ['Standard_D4s_v5', 'Standard_D8s_v5', 'Standard_D16s_v5', 'Standard_E4s_v5', 'Standard_E8s_v5'],
  a10: ['Standard_NV6ads_A10_v5', 'Standard_NV12ads_A10_v5', 'Standard_NV18ads_A10_v5', 'Standard_NV36ads_A10_v5'],
  v710: ['Standard_NV4ads_V710_v5', 'Standard_NV8ads_V710_v5', 'Standard_NV12ads_V710_v5', 'Standard_NV24ads_V710_v5'],
};

const CUSTOM_SKU_SENTINEL = '__custom__';

const defaultVmSizeFor = (series: GpuSeries): string => CURATED_VM_SIZES[series][0];

// NC-1102 (Feat 2.0): curated suggestions for the packerConfig fields —
// the server stays tolerant (override via "Andere…"), the lists just stop
// typo-SKUs/base-images from admins. Build sizes are non-GPU by policy
// (NC-961: Builds laufen ohne GPU; GPU nur zur Laufzeit via runtimeConfig).
const BUILD_VM_SIZES = ['Standard_D4s_v5', 'Standard_D8s_v5', 'Standard_D16s_v5', 'Standard_B2s'] as const;
const BASE_IMAGES = ['Win11-23H2-Enterprise', 'Win11-24H2-Enterprise', 'WinServer2022-Datacenter'] as const;

// NC-1102: template/variables file names follow a fixed convention —
// generate-template.ps1 writes `<id>-generated.pkr.hcl` and build-images.yml
// passes `variables/<id>.pkrvars.hcl`. Derive them from the workload id and
// only expose free-text behind an explicit "abweichend" toggle.
const deriveTemplateFile = (workloadId: string): string =>
  workloadId ? `${workloadId}-generated.pkr.hcl` : '';
const deriveVariablesFile = (workloadId: string): string =>
  workloadId ? `variables/${workloadId}.pkrvars.hcl` : '';

const summarizeRuntimeConfig = (rc: RuntimeConfig): string => {
  const seriesLabel = GPU_SERIES_OPTIONS.find((o) => o.value === rc.gpuSeries)?.label ?? rc.gpuSeries;
  return `${seriesLabel} · ${rc.vmSize} · ${REGION_BY_SERIES[rc.gpuSeries]}`;
};

interface ApplicationFormData {
  id: string;
  displayName: string;
  category: ApplicationCategory;
  silentArgs: string;
  requiresReboot: boolean;
  installOrder: number;
}

export default function WorkloadEditorPage() {
  const { workloadId } = useParams<{ workloadId: string }>();
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const isEditMode = workloadId !== 'new' && !!workloadId;

  // Phase 3: Build-Image dialog state
  const [buildDialogOpen, setBuildDialogOpen] = useState(false);

  // NC-636: Tab state. `showTrainingTab` gates the new "Trainings-Daten" tab.
  // When false, the page renders identically to its pre-NC-636 layout.
  const showTrainingTab = NC_636_TRAINING_FILES_ENABLED && workloadId !== 'new' && !!workloadId;
  const [activeTab, setActiveTab] = useState(0);

  const canBuild = isEditMode && hasRole('operator') && isBuildable(workloadId);

  // NC-984 (F2): buildable-but-not-promotable workloads produce images
  // that stay in staging forever. Surface that up front so the operator isn't
  // surprised by a missing Promote button after a 60–90 min build.
  const buildStagingOnly = canBuild && isStagingOnly(workloadId);

  // M20-D3 (NC-974): runtimeConfig is admin-editable only. Everyone else sees a
  // read-only summary of the current value.
  const isAdmin = hasRole('admin');

  // NC-1291: POST /api/workloads is admin-only (workloads-create/index.ts
  // requireRole(req, 'admin')). Create mode is reachable by URL for anyone, so
  // a viewer/operator used to see a fully usable form that only failed with a
  // 403 after filling it in. Block the affordance, not just the outcome.
  const createBlocked = !isEditMode && !isAdmin;

  // Form state
  const [id, setId] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<WorkloadCategory>('cam');
  const [isActive, setIsActive] = useState(true);
  const [applications, setApplications] = useState<ApplicationFormData[]>([]);
  const [templateFile, setTemplateFile] = useState('');
  const [variablesFile, setVariablesFile] = useState('');
  const [baseImage, setBaseImage] = useState('Win11-23H2-Enterprise');
  const [vmSize, setVmSize] = useState('Standard_D4s_v5');
  // NC-1102: template/variables normally follow the id-derived convention;
  // the toggle exposes free-text for legacy/abweichende Dateinamen.
  const [filesOverride, setFilesOverride] = useState(false);

  // M20-D3 (NC-974): run-time VM override state. `runtimeOverride` gates whether
  // a runtimeConfig is sent at all; when off the run-lifecycle uses the
  // config.json default. `runtimeVmSizeCustom` switches the SKU field from the
  // curated dropdown to a free-text input.
  const [runtimeOverride, setRuntimeOverride] = useState(false);
  const [runtimeGpuSeries, setRuntimeGpuSeries] = useState<GpuSeries>('none');
  const [runtimeVmSize, setRuntimeVmSize] = useState<string>(defaultVmSizeFor('none'));
  const [runtimeVmSizeCustom, setRuntimeVmSizeCustom] = useState(false);

  // Application dialog state
  const [appDialogOpen, setAppDialogOpen] = useState(false);
  const [currentApp, setCurrentApp] = useState<ApplicationFormData | null>(null);
  const [appEditIndex, setAppEditIndex] = useState<number | null>(null);

  const { data: workload, loading, error: fetchError } = useApiQuery(
    () => workloadsApi.getById(workloadId!),
    [workloadId],
    { enabled: isEditMode }
  );

  // Populate form when workload data loads
  useEffect(() => {
    if (workload) {
      setId(workload.id);
      setName(workload.name);
      setDescription(workload.description);
      setCategory(workload.category);
      setIsActive(workload.isActive);
      setTemplateFile(workload.packerConfig.templateFile);
      setVariablesFile(workload.packerConfig.variablesFile);
      setBaseImage(workload.packerConfig.baseImage);
      setVmSize(workload.packerConfig.vmSize);
      // NC-1102: legacy docs may carry non-convention file names — surface
      // them via the override toggle instead of silently rewriting them.
      setFilesOverride(
        workload.packerConfig.templateFile !== deriveTemplateFile(workload.id) ||
          workload.packerConfig.variablesFile !== deriveVariablesFile(workload.id)
      );

      // M20-D3 (NC-974): hydrate the run-time VM override from the workload.
      const rc = workload.runtimeConfig;
      if (rc) {
        setRuntimeOverride(true);
        setRuntimeGpuSeries(rc.gpuSeries);
        setRuntimeVmSize(rc.vmSize);
        setRuntimeVmSizeCustom(!CURATED_VM_SIZES[rc.gpuSeries].includes(rc.vmSize));
      } else {
        setRuntimeOverride(false);
        setRuntimeGpuSeries('none');
        setRuntimeVmSize(defaultVmSizeFor('none'));
        setRuntimeVmSizeCustom(false);
      }

      const appsData: ApplicationFormData[] = (workload.applications ?? []).map((app) => ({
        id: app.id,
        displayName: app.displayName,
        category: app.category,
        silentArgs: app.installConfig.silentArgs,
        requiresReboot: app.installConfig.requiresReboot,
        installOrder: app.installConfig.installOrder,
      }));
      setApplications(appsData);
    }
  }, [workload]);

  // NC-1102: while the convention is active, keep the file names in sync with
  // the (create-mode editable) workload id.
  useEffect(() => {
    if (!filesOverride) {
      setTemplateFile(deriveTemplateFile(id));
      setVariablesFile(deriveVariablesFile(id));
    }
  }, [id, filesOverride]);

  const saveMutation = useApiMutation<void>(
    async () => {
      if (!id || !name || !category) {
        throw new Error('Bitte füllen Sie alle Pflichtfelder aus');
      }
      // NC-1102: the selects allow explicit overrides — but never empty values.
      if (!vmSize || !baseImage) {
        throw new Error('Bitte VM-Größe (Build) und Basis-Image angeben');
      }
      if (filesOverride && (!templateFile || !variablesFile)) {
        throw new Error('Abweichende Packer-Dateinamen dürfen nicht leer sein');
      }

      const apps: Application[] = applications.map((app) => ({
        id: app.id,
        displayName: app.displayName,
        category: app.category,
        installer: null,
        installConfig: {
          silentArgs: app.silentArgs,
          requiresReboot: app.requiresReboot,
          installOrder: app.installOrder,
        },
      }));

      const packer: PackerConfig = {
        templateFile,
        variablesFile,
        baseImage,
        vmSize,
      };

      // M20-D3 (NC-974): only send runtimeConfig when the admin has enabled the
      // override — otherwise the run-lifecycle falls back to the config.json
      // default. Sending an empty/partial object would fail BE validation (400).
      const runtimeConfig: RuntimeConfig | undefined = runtimeOverride
        ? { vmSize: runtimeVmSize.trim(), gpuSeries: runtimeGpuSeries }
        : undefined;

      try {
        if (isEditMode) {
          const workloadData: Partial<WorkloadDefinition> = {
            name,
            description,
            category,
            isActive,
            applications: apps,
            packerConfig: packer,
            ...(runtimeConfig && { runtimeConfig }),
          };
          await workloadsApi.update(workloadId!, workloadData);
        } else {
          const workloadData = {
            name,
            description,
            category,
            isActive,
            applications: apps,
            packerConfig: packer,
            id,
            pk: id,
            ...(runtimeConfig && { runtimeConfig }),
          };
          await workloadsApi.create(workloadData);
        }
      } catch (err) {
        // Surface the BE's descriptive validation message (e.g. the
        // runtimeConfig whitelist 400) instead of the raw axios
        // "Request failed with status code 400" string (NC-974).
        throw new Error(extractErrorMessage(err));
      }
    },
    {
      // NC-1296 Soll 1: a freshly created workload continues straight into the
      // P1 Steckbrief wizard (#1279) instead of dropping the user back on the
      // list — the Steckbrief is what makes the workload documented, and the
      // moment right after creation is the only one where the author has all
      // the context in their head. Edit mode keeps its existing behaviour.
      //
      // NOT a gate: the wizard can be left at any time and the workload stays
      // valid (design spec 2 "Nicht-Ziele"). This only changes where the flow
      // continues, never whether the creation counted.
      onSuccess: () =>
        navigate(isEditMode ? '/workloads' : `/workloads/${encodeURIComponent(id)}/steckbrief`),
    }
  );

  const handleSave = async () => {
    await saveMutation.mutate(undefined as void);
  };

  // M20-D3 (NC-974): switching series resets the SKU to that series' default
  // preset so the vmSize<->gpuSeries pairing stays consistent (avoids a BE 400).
  const handleRuntimeSeriesChange = (series: GpuSeries) => {
    setRuntimeGpuSeries(series);
    setRuntimeVmSize(defaultVmSizeFor(series));
    setRuntimeVmSizeCustom(false);
  };

  const handleRuntimeVmSizeSelect = (value: string) => {
    if (value === CUSTOM_SKU_SENTINEL) {
      setRuntimeVmSizeCustom(true);
    } else {
      setRuntimeVmSizeCustom(false);
      setRuntimeVmSize(value);
    }
  };

  const handleAddApplication = () => {
    setCurrentApp({
      id: '',
      displayName: '',
      category: 'primary',
      silentArgs: '',
      requiresReboot: false,
      installOrder: applications.length + 1,
    });
    setAppEditIndex(null);
    setAppDialogOpen(true);
  };

  const handleEditApplication = (index: number) => {
    setCurrentApp({ ...applications[index] });
    setAppEditIndex(index);
    setAppDialogOpen(true);
  };

  const handleDeleteApplication = (index: number) => {
    const updatedApps = applications.filter((_, i) => i !== index);
    setApplications(updatedApps);
  };

  const handleSaveApplication = () => {
    if (!currentApp || !currentApp.id || !currentApp.displayName) {
      return;
    }

    if (appEditIndex !== null) {
      const updatedApps = [...applications];
      updatedApps[appEditIndex] = currentApp;
      setApplications(updatedApps);
    } else {
      setApplications([...applications, currentApp]);
    }

    setAppDialogOpen(false);
    setCurrentApp(null);
    setAppEditIndex(null);
  };

  const error = fetchError || saveMutation.error;

  // NC-983 (F1): derive build-readiness from the live form state so the operator
  // sees which build essentials are still open BEFORE dispatching a 60–90 min
  // build (no backend call — reused as the NC-972 wizard's readiness step).
  const readiness = canBuild
    ? evaluateBuildReadiness({
        applicationsCount: applications.length,
        baseImage,
        templateFile,
        variablesFile,
      })
    : null;

  if (loading) {
    return <TableSkeleton rows={10} columns={2} />;
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate('/workloads')} sx={{ mr: 2 }}>
          <BackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ flexGrow: 1 }}>
          {isEditMode ? 'Workload bearbeiten' : 'Neuer Workload'}
        </Typography>
        {canBuild && (
          <Box display="flex" alignItems="center" gap={1}>
            {buildStagingOnly && (
              <Tooltip title="Images dieses Workloads bleiben in staging und können nicht nach production promotet werden.">
                <Chip
                  label="Nur staging"
                  size="small"
                  color="default"
                  variant="outlined"
                  data-testid="staging-only-chip"
                />
              </Tooltip>
            )}
            <Button
              variant="outlined"
              color="primary"
              startIcon={<BuildIcon />}
              onClick={() => setBuildDialogOpen(true)}
              aria-label="Image bauen"
            >
              Image bauen
            </Button>
          </Box>
        )}
      </Box>

      {canBuild && workloadId && (
        <BuildImageDialog
          open={buildDialogOpen}
          workloadId={workloadId}
          workloadName={name || workloadId}
          stagingOnly={buildStagingOnly}
          onClose={() => setBuildDialogOpen(false)}
          onDispatched={(buildId) => {
            setBuildDialogOpen(false);
            navigate(`/images/${buildId}?workloadId=${encodeURIComponent(workloadId)}`);
          }}
        />
      )}

      {error && (
        <ErrorMessage
          message={error}
          title="Fehler"
        />
      )}

      {/* NC-983 (F1): build-readiness checklist, visible for buildable workloads
          on the general tab so open points surface before "Image bauen". */}
      {readiness && (!showTrainingTab || activeTab === 0) && (
        <BuildReadinessChecklist checks={readiness.checks} allReady={readiness.allReady} />
      )}

      {showTrainingTab && (
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          <Tab label="Allgemein" value={0} />
          <Tab label="Trainings-Daten" value={1} />
        </Tabs>
      )}

      {showTrainingTab && activeTab === 1 && workloadId && (
        <TrainingFilesTab workloadId={workloadId} />
      )}

      {(!showTrainingTab || activeTab === 0) && (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Grundeinstellungen
          </Typography>

          {/* NC-1296 Soll 2: orientation before the fields, and only while
              creating. Soll 1 (PR #1345) sends a freshly created workload
              straight into the Steckbrief wizard — landing there unannounced is
              disorienting, so the form says up front what happens after saving.
              In edit mode this would be noise: nothing changes there. */}
          {!isEditMode && (
            <Alert severity="info" sx={{ mb: 3 }} data-testid="create-mode-intro">
              Hier stehen die <strong>technischen</strong> Angaben: ID und Kategorie, die
              Packer-Konfiguration für den Image-Build und optional eine abweichende
              VM-Größe für die Laufzeit. Für die üblichen Fälle passen die Vorschlagswerte.
              Nach dem Speichern geht es direkt weiter in den <strong>Steckbrief</strong> —
              dort werden Zweck, Verantwortung und Nutzung beschrieben. Der Steckbrief kann
              jederzeit verlassen und später ergänzt werden.
            </Alert>
          )}

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label="ID"
                value={id}
                onChange={(e) => setId(e.target.value)}
                fullWidth
                required
                disabled={isEditMode}
                helperText="Eindeutiger Bezeichner (z.B. 'cam', 'bim', 'it-dev')"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Kategorie</InputLabel>
                <Select value={category} onChange={(e) => setCategory(e.target.value as WorkloadCategory)}>
                  <MenuItem value="cam">CAM</MenuItem>
                  <MenuItem value="bim">BIM</MenuItem>
                  <MenuItem value="it">IT Development</MenuItem>
                </Select>
                {/* NC-1296 Soll 2: the only named mandatory field that carried no
                    explanation at all. It is not cosmetic — the category drives
                    the grouping and the colour coding in the workload list, so a
                    wrong pick is visible everywhere afterwards. */}
                <FormHelperText>
                  Fachliche Einordnung — steuert Gruppierung und Farbe in der Workload-Liste.
                  Nachträglich änderbar.
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                required
                helperText="Anzeigename (z.B. 'CAM - HyperMill')"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Beschreibung"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                fullWidth
                multiline
                rows={3}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
                label="Aktiv (kann für neue Runs ausgewählt werden)"
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 4 }} />

          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Anwendungen</Typography>
            <Button startIcon={<AddIcon />} onClick={handleAddApplication} variant="outlined">
              Anwendung hinzufügen
            </Button>
          </Box>

          {applications.length === 0 ? (
            <Typography color="text.secondary" align="center" py={3}>
              Keine Anwendungen definiert
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Reihenfolge</TableCell>
                  <TableCell>Anwendung</TableCell>
                  <TableCell>Kategorie</TableCell>
                  <TableCell>Silent Args</TableCell>
                  <TableCell align="center">Neustart</TableCell>
                  <TableCell align="right">Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {applications
                  .sort((a, b) => a.installOrder - b.installOrder)
                  .map((app, index) => (
                    <TableRow key={app.id}>
                      <TableCell>{app.installOrder}</TableCell>
                      <TableCell>{app.displayName}</TableCell>
                      <TableCell>{app.category}</TableCell>
                      <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {app.silentArgs}
                      </TableCell>
                      <TableCell align="center">{app.requiresReboot ? 'Ja' : 'Nein'}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Bearbeiten">
                          <IconButton size="small" onClick={() => handleEditApplication(index)}>
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Löschen">
                          <IconButton size="small" color="error" onClick={() => handleDeleteApplication(index)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          )}

          <Divider sx={{ my: 4 }} />

          <Typography variant="h6" gutterBottom>
            Packer-Konfiguration
          </Typography>

          <Grid container spacing={3}>
            {/* NC-1102: Dateinamen folgen der Konvention aus der Workload-ID;
                Freitext nur hinter dem expliziten "abweichend"-Toggle. */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={filesOverride}
                    onChange={(e) => setFilesOverride(e.target.checked)}
                    inputProps={{ 'data-testid': 'files-override-switch' } as any}
                  />
                }
                label="Abweichende Packer-Dateinamen verwenden"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Template-Datei"
                value={templateFile}
                onChange={(e) => setTemplateFile(e.target.value)}
                fullWidth
                disabled={!filesOverride}
                helperText={
                  filesOverride
                    ? "Abweichender Dateiname, z.B. 'cam-hypermill.pkr.hcl'"
                    : 'Automatisch aus der Workload-ID abgeleitet.'
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Variablen-Datei"
                value={variablesFile}
                onChange={(e) => setVariablesFile(e.target.value)}
                fullWidth
                disabled={!filesOverride}
                helperText={
                  filesOverride
                    ? "Abweichender Dateiname, z.B. 'variables/cam.pkrvars.hcl'"
                    : 'Automatisch aus der Workload-ID abgeleitet.'
                }
              />
            </Grid>

            <SelectWithCustom
              label="Basis-Image"
              value={baseImage}
              options={BASE_IMAGES}
              onChange={setBaseImage}
              helperText="Vorschlagsliste — 'Andere…' für eigene Basis."
              customLabel="Eigenes Basis-Image"
              customHelperText="Name der Basis-Image-Definition, z.B. 'Win11-23H2-Enterprise'."
            />

            <SelectWithCustom
              label="VM-Größe (Build)"
              value={vmSize}
              options={BUILD_VM_SIZES}
              onChange={setVmSize}
              helperText="Packer-BUILD-SKU (nicht die Laufzeit-Größe, s.u.) — non-GPU per NC-961."
              customLabel="Eigene Build-SKU"
              customHelperText="Azure-SKU, z.B. 'Standard_D8s_v5' (nur Buchstaben, Ziffern, _)."
            />
          </Grid>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h6" gutterBottom>
            Laufzeit-VM (Azure)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Größe und GPU-Serie der Trainings-VMs zur Laufzeit. Region und Subscription
            werden aus der GPU-Serie abgeleitet. {isAdmin ? '' : 'Nur Administratoren können diese Einstellung ändern.'}
          </Typography>

          {!isAdmin ? (
            <Typography variant="body2" data-testid="runtime-vm-readonly">
              {runtimeOverride && workload?.runtimeConfig
                ? summarizeRuntimeConfig(workload.runtimeConfig)
                : 'Standard-Größe (aus Konfiguration)'}
            </Typography>
          ) : (
            <>
              <FormControlLabel
                control={
                  <Switch
                    checked={runtimeOverride}
                    onChange={(e) => setRuntimeOverride(e.target.checked)}
                  />
                }
                label="Laufzeit-VM für diesen Workload überschreiben"
              />
              {!runtimeOverride ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Aus: Die Laufzeit nutzt die Standard-Größe aus der Konfiguration.
                </Typography>
              ) : (
                <Grid container spacing={3} sx={{ mt: 0 }}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>GPU-Serie</InputLabel>
                      <Select
                        label="GPU-Serie"
                        value={runtimeGpuSeries}
                        onChange={(e) => handleRuntimeSeriesChange(e.target.value as GpuSeries)}
                      >
                        {GPU_SERIES_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Abgeleitete Region"
                      value={REGION_BY_SERIES[runtimeGpuSeries]}
                      fullWidth
                      disabled
                      inputProps={{ 'data-testid': 'runtime-region' }}
                      helperText="Kontingent: 100 vCPUs je VM-Familie in der Ziel-Region."
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      label="VM-Größe (Laufzeit)"
                      value={runtimeVmSizeCustom ? CUSTOM_SKU_SENTINEL : runtimeVmSize}
                      onChange={(e) => handleRuntimeVmSizeSelect(e.target.value)}
                      fullWidth
                      helperText="Kuratierte Azure-SKU oder eigene angeben."
                    >
                      {CURATED_VM_SIZES[runtimeGpuSeries].map((sku) => (
                        <MenuItem key={sku} value={sku}>
                          {sku}
                        </MenuItem>
                      ))}
                      <MenuItem value={CUSTOM_SKU_SENTINEL}>Andere SKU…</MenuItem>
                    </TextField>
                  </Grid>

                  {runtimeVmSizeCustom && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Eigene SKU"
                        value={runtimeVmSize}
                        onChange={(e) => setRuntimeVmSize(e.target.value)}
                        fullWidth
                        helperText="Azure-SKU, z.B. 'Standard_NV6ads_A10_v5' (nur Buchstaben, Ziffern, _)."
                      />
                    </Grid>
                  )}
                </Grid>
              )}
            </>
          )}

          {createBlocked && (
            <Alert severity="info" sx={{ mt: 3 }} data-testid="create-requires-admin">
              Neue Workloads koennen nur Administratoren anlegen. Die Felder sind
              zur Ansicht offen, das Speichern ist deaktiviert.
            </Alert>
          )}

          <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
            <Button variant="outlined" onClick={() => navigate('/workloads')}>
              Abbrechen
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saveMutation.loading || createBlocked}
            >
              {saveMutation.loading ? 'Speichern...' : 'Speichern'}
            </Button>
          </Box>
        </CardContent>
      </Card>
      )}

      {/* Application Edit Dialog */}
      <Dialog open={appDialogOpen} onClose={() => setAppDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {appEditIndex !== null ? 'Anwendung bearbeiten' : 'Anwendung hinzufügen'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                label="ID"
                value={currentApp?.id || ''}
                onChange={(e) => setCurrentApp({ ...currentApp!, id: e.target.value })}
                fullWidth
                required
                disabled={appEditIndex !== null}
                helperText="z.B. 'hypermill', 'inventor'"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Anzeigename"
                value={currentApp?.displayName || ''}
                onChange={(e) => setCurrentApp({ ...currentApp!, displayName: e.target.value })}
                fullWidth
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Kategorie</InputLabel>
                <Select
                  value={currentApp?.category || 'primary'}
                  onChange={(e) =>
                    setCurrentApp({ ...currentApp!, category: e.target.value as ApplicationCategory })
                  }
                >
                  <MenuItem value="primary">Primary</MenuItem>
                  <MenuItem value="support">Support</MenuItem>
                  <MenuItem value="optional">Optional</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Installations-Reihenfolge"
                type="number"
                value={currentApp?.installOrder || 1}
                onChange={(e) =>
                  setCurrentApp({ ...currentApp!, installOrder: parseInt(e.target.value) || 1 })
                }
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Silent Installation Args"
                value={currentApp?.silentArgs || ''}
                onChange={(e) => setCurrentApp({ ...currentApp!, silentArgs: e.target.value })}
                fullWidth
                helperText="z.B. '/S /D=C:\\Program Files\\App'"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={currentApp?.requiresReboot || false}
                    onChange={(e) => setCurrentApp({ ...currentApp!, requiresReboot: e.target.checked })}
                  />
                }
                label="Erfordert Neustart nach Installation"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAppDialogOpen(false)}>Abbrechen</Button>
          <Button onClick={handleSaveApplication} variant="contained">
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
